import Button from 'ext-ui/components/Button/Button'
import ButtonGroup from 'ext-ui/components/ButtonGroup/ButtonGroup'
import Modal from 'ext-ui/components/Modal/Modal'
import React, { ReactElement } from 'react'
import type { PhysicalCardLifecycleModalState } from 'src/feature/PhysicalCardLifecycle/physicalCardLifecycle.types'
import { useTranslate } from 'src/localization/Trns'

import style from './PhysicalCardLifecycleModal.style'

type PhysicalCardLifecycleModalProps = {
  state: PhysicalCardLifecycleModalState | undefined
  onClose: () => void
  onConfirm: () => void
  canFreezeAfterFraud?: boolean
  onFreeze?: () => void
  onViewReplacementCard?: (creditCardId: string) => void
}

type ActionContent = {
  confirmationTitle: string
  confirmationBody: string
  confirmLabel: string
  processingTitle: string
  processingBody: string
  successTitle: string
  successBody: string
  severe: boolean
}

export default function PhysicalCardLifecycleModal({
  state,
  onClose,
  onConfirm,
  canFreezeAfterFraud = false,
  onFreeze,
  onViewReplacementCard,
}: PhysicalCardLifecycleModalProps): ReactElement {
  const translate = useTranslate()

  const content = ((): ActionContent => {
    switch (state?.action) {
      case 'lostOrStolen':
        return {
          confirmationTitle: translate('Report this card lost or stolen?'),
          confirmationBody: translate(
            "This will lock the card while your bank reviews the request. You can't undo this action in the app.",
          ),
          confirmLabel: translate('Report card'),
          processingTitle: translate('Report submitted'),
          processingBody: translate('Your request is being recorded.'),
          successTitle: translate('Card reported'),
          successBody: translate(
            'Your card was reported lost or stolen. Your bank will be in touch shortly about issuing a replacement card.',
          ),
          severe: true,
        }
      case 'reportFraud':
        return {
          confirmationTitle: translate('Report fraud on this card?'),
          confirmationBody: translate(
            "Flagged activity will go to the issuer's fraud team for review. The card keeps working unless you also freeze it.",
          ),
          confirmLabel: translate('Report fraud'),
          processingTitle: translate('Fraud report submitted'),
          processingBody: translate('Your fraud report is being processed.'),
          successTitle: translate('Fraud reported'),
          successBody: translate(
            'The issuer received your fraud report. Your card is still active unless you freeze it separately.',
          ),
          severe: false,
        }
      case 'cancel':
        return {
          confirmationTitle: translate('Cancel this physical card?'),
          confirmationBody: translate(
            "This will permanently close the card and all of its associated virtual cards. This can't be undone.",
          ),
          confirmLabel: translate('Cancel card'),
          processingTitle: translate('Cancellation in progress'),
          processingBody: translate(
            'Your physical card cancellation is being processed.',
          ),
          successTitle: translate('Card canceled'),
          successBody: translate(
            'Your physical card and its associated virtual cards are closed.',
          ),
          severe: true,
        }
      case 'replace':
      default:
        return {
          confirmationTitle: translate('Replace this physical card?'),
          confirmationBody: translate(
            "This will lock the card while your bank reviews the request. You can't undo this action in the app.",
          ),
          confirmLabel: translate('Request replacement'),
          processingTitle: translate('Replacement requested'),
          processingBody: translate('Your request is being recorded.'),
          successTitle: translate('Replacement requested'),
          successBody: translate(
            'Your replacement request was submitted. Your bank will be in touch shortly about issuing a replacement card.',
          ),
          severe: false,
        }
    }
  })()

  const phase = state?.phase ?? 'confirmation'
  const isSubmitting = phase === 'submitting'
  const isConfirmation = phase === 'confirmation' || isSubmitting
  const isProcessing = phase === 'processing'
  const isPendingApproval =
    isProcessing && state?.operation?.status === 'PENDING_APPROVAL'
  const isSuccess = phase === 'succeeded'
  const isFailure = phase === 'failed' || phase === 'error'
  const canRetry =
    phase === 'error' &&
    (state?.errorKind === 'transport' || state?.errorKind === 'retryable')
  const replacementCardId = state?.operation?.result?.resultingCreditCardId
  const showReplacementLink =
    isSuccess &&
    (state?.action === 'replace' || state?.action === 'lostOrStolen') &&
    replacementCardId !== undefined &&
    replacementCardId !== state.operation?.subjectCreditCardId

  const title = (() => {
    if (isConfirmation) return content.confirmationTitle
    if (isPendingApproval) return translate('Approval required')
    if (isProcessing) return content.processingTitle
    if (isSuccess) return content.successTitle
    if (phase === 'canceled') return translate('Operation canceled')
    return translate("We couldn't complete this request")
  })()

  const statusBody = (() => {
    if (isPendingApproval) {
      return translate(
        'This request is waiting for approval before it can be processed.',
      )
    }
    if (isProcessing) return content.processingBody
    if (isSuccess) return content.successBody
    if (phase === 'canceled') {
      return translate('No changes were made to this card.')
    }

    switch (state?.errorKind) {
      case 'uncertain':
        return translate(
          'The issuer has not confirmed the outcome. Refresh the card before taking another action.',
        )
      case 'operationActive':
        return translate(
          'Another card operation is already in progress. Wait for it to finish before trying again.',
        )
      case 'stale':
        return translate(
          'The card changed while this dialog was open. Refresh and try again.',
        )
      case 'unavailable':
        return translate(
          'This action is no longer available for the current card state.',
        )
      case 'rejected':
        return translate(
          'The issuer could not complete this request. Review the card before trying again.',
        )
      default:
        return phase === 'failed'
          ? translate(
              'The issuer could not complete this request. Review the card before trying again.',
            )
          : translate('Something went wrong. You can retry this exact request.')
    }
  })()

  const footer = isConfirmation ? (
    <ButtonGroup>
      <Button
        variant="no-style"
        onClick={onClose}
        disabled={isSubmitting}
        data-testid="lifecycle-dismiss"
      >
        {state?.action === 'cancel'
          ? translate('Keep card')
          : translate('Cancel')}
      </Button>
      <Button
        variant={content.severe ? 'severe' : 'primary'}
        dropShadow={false}
        onClick={onConfirm}
        loading={isSubmitting}
        disabled={isSubmitting}
        data-testid="lifecycle-confirm"
      >
        {content.confirmLabel}
      </Button>
    </ButtonGroup>
  ) : (
    <ButtonGroup>
      {canRetry && (
        <Button variant="secondary" onClick={onConfirm}>
          {translate('Retry')}
        </Button>
      )}
      {isSuccess &&
        state?.action === 'reportFraud' &&
        canFreezeAfterFraud &&
        onFreeze && (
          <Button variant="secondary" onClick={onFreeze}>
            {translate('Freeze card')}
          </Button>
        )}
      {showReplacementLink && onViewReplacementCard && (
        <Button
          variant="secondary"
          onClick={() => onViewReplacementCard(replacementCardId)}
        >
          {translate('View replacement card')}
        </Button>
      )}
      <Button variant="primary" onClick={onClose}>
        {translate('Close')}
      </Button>
    </ButtonGroup>
  )

  const politeStatusBody = ((): string | undefined => {
    if (isSubmitting) return translate('Submitting request.')
    if (!isConfirmation && !isFailure) return statusBody
    return undefined
  })()
  const alertBody = isFailure ? statusBody : undefined

  return (
    <Modal
      open={state !== undefined}
      onClose={onClose}
      closable={!isSubmitting}
      title={title}
      footer={footer}
    >
      <div css={style}>
        {isConfirmation && <p>{content.confirmationBody}</p>}
        <div
          className="PhysicalCardLifecycleModal-status"
          role="status"
          aria-atomic="true"
        >
          {politeStatusBody && <p>{politeStatusBody}</p>}
        </div>
        <div
          className="PhysicalCardLifecycleModal-status"
          role="alert"
          aria-atomic="true"
        >
          {alertBody && <p>{alertBody}</p>}
        </div>
      </div>
    </Modal>
  )
}
